import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    CENTER_SERVICE_AVAILABILITY_STATUS,
    getCenterServiceAvailabilityStatus,
} from '@/utils/centerServiceAvailability';

interface DinnerAccessGuardProps {
    children: ReactNode;
}

export function DinnerAccessGuard({ children }: DinnerAccessGuardProps) {
    const navigate = useNavigate();
    const { isLoggedIn, isLoading: authLoading, checkAuth } = useAuth();
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        if (authLoading) {
            return;
        }

        if (!isLoggedIn) {
            setIsChecking(false);
            return;
        }

        let isMounted = true;

        const verifyDinnerAccess = async () => {
            setIsChecking(true);

            if (getCenterServiceAvailabilityStatus() === CENTER_SERVICE_AVAILABILITY_STATUS.SERVICE_RESERVATION_BLOCKED) {
                navigate('/reservation-time-restricted', { replace: true });
                return;
            }

            const refreshedUser = await checkAuth({ showLoading: false });

            if (!isMounted) {
                return;
            }

            if (refreshedUser?.serviceAccess?.dinnerEnabled === false) {
                navigate('/dinner/access-restricted', { replace: true });
                return;
            }

            if (refreshedUser?.todayDinnerReservation) {
                navigate('/dinner/already-reserved', { replace: true });
                return;
            }

            setIsChecking(false);
        };

        verifyDinnerAccess();

        return () => {
            isMounted = false;
        };
    }, [authLoading, checkAuth, isLoggedIn, navigate]);

    if (authLoading || isChecking) {
        return <div style={{ width: '100vw', height: '100vh', backgroundColor: '#ffffff' }} aria-hidden="true" />;
    }

    return <>{children}</>;
}
